import { request } from "$/lib/api"
import {
  CreateDiscountCode,
  DiscountCodeStats,
  DiscountCodeWithUsageCount,
  ExcludableDiscountPartAction,
  Route,
  UpdateDiscountCode,
  ValidatedDiscountCode,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

interface DiscountCodeFilters {
  storeId: string
}

async function fetchDiscountCodes(
  storeId: string,
): Promise<DiscountCodeWithUsageCount[]> {
  return await request<DiscountCodeWithUsageCount[]>({
    url: populateRoute(
      Route.DISCOUNT_CODE_CONTROLLER__FIND_MANY_BY_STORE_ID,
      storeId,
    ),
    method: "GET",
  })
}

async function fetchDiscountCodeStats(
  storeId: string,
): Promise<DiscountCodeStats[]> {
  return await request<DiscountCodeStats[]>({
    url: populateRoute(Route.DISCOUNT_CODE_CONTROLLER__STATS, storeId),
    method: "GET",
  })
}

async function fetchExcludablePartActions(
  storeId: string,
): Promise<ExcludableDiscountPartAction[]> {
  return await request<ExcludableDiscountPartAction[]>({
    url: populateRoute(
      Route.DISCOUNT_CODE_CONTROLLER__EXCLUDABLE_PART_ACTIONS,
      storeId,
    ),
    method: "GET",
  })
}

async function createDiscountCode(data: CreateDiscountCode, storeId: string) {
  return await request<string>({
    url: populateRoute(Route.DISCOUNT_CODE_CONTROLLER__CREATE, storeId),
    method: "POST",
    data,
  })
}

async function deleteDiscountCode(id: string) {
  return await request<void>({
    url: populateRoute(Route.DISCOUNT_CODE_CONTROLLER__DELETE, id),
    method: "DELETE",
  })
}

async function updateDiscountCode(data: UpdateDiscountCode, id: string) {
  return await request<void>({
    url: populateRoute(Route.DISCOUNT_CODE_CONTROLLER__UPDATE, id),
    method: "PATCH",
    data,
  })
}

async function validateDiscountCode(
  code: string,
  storeId: string,
): Promise<ValidatedDiscountCode> {
  return await request<ValidatedDiscountCode>({
    url: populateRoute(Route.DISCOUNT_CODE_CONTROLLER__VALIDATE, storeId),
    method: "POST",
    data: { code },
  })
}

async function applyDiscountCodeToJob(codeId: string, jobId: string) {
  return await request<void>({
    url: populateRoute(
      Route.DISCOUNT_CODE_CONTROLLER__APPLY_TO_JOB,
      codeId,
      jobId,
    ),
    method: "POST",
  })
}

async function removeDiscountCodeFromJob(jobId: string) {
  return await request<void>({
    url: populateRoute(Route.DISCOUNT_CODE_CONTROLLER__REMOVE_FROM_JOB, jobId),
    method: "DELETE",
  })
}

export function useDiscountCodeMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () => {
    await queryClient.invalidateQueries(discountCodeQueries.list({ storeId }))
    await queryClient.invalidateQueries(discountCodeQueries.stats(storeId))
  }

  const createMutation = useMutation({
    mutationFn: (data: CreateDiscountCode) => createDiscountCode(data, storeId),
    onSettled: invalidateQueries,
  })

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => {
      const promises = ids.map((id) => deleteDiscountCode(id))
      return Promise.all(promises)
    },
    onSettled: invalidateQueries,
  })

  const updateMutation = useMutation({
    mutationFn: ({ data, id }: { data: UpdateDiscountCode; id: string }) =>
      updateDiscountCode(data, id),
    onSettled: invalidateQueries,
  })

  return { createMutation, deleteMutation, updateMutation }
}

export function useValidateDiscountCode(storeId: string) {
  return useMutation({
    mutationFn: (code: string) => validateDiscountCode(code, storeId),
  })
}

export function useApplyDiscountCodeToJob() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ codeId, jobId }: { codeId: string; jobId: string }) =>
      applyDiscountCodeToJob(codeId, jobId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      queryClient.invalidateQueries({ queryKey: ["receipt"] })
    },
  })
}

export function useRemoveDiscountCodeFromJob() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (jobId: string) => removeDiscountCodeFromJob(jobId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      queryClient.invalidateQueries({ queryKey: ["receipt"] })
    },
  })
}

const discountCodeQueries = {
  all: () => ["discount-code"],
  lists: () => [...discountCodeQueries.all(), "list"],
  list: (filters: DiscountCodeFilters) =>
    queryOptions({
      queryKey: [...discountCodeQueries.lists(), filters],
      queryFn: () => fetchDiscountCodes(filters.storeId),
    }),
  stats: (storeId: string) =>
    queryOptions({
      queryKey: [...discountCodeQueries.all(), "stats", storeId],
      queryFn: () => fetchDiscountCodeStats(storeId),
    }),
  excludablePartActions: (storeId: string) =>
    queryOptions({
      queryKey: [
        ...discountCodeQueries.all(),
        "excludable-part-actions",
        storeId,
      ],
      queryFn: () => fetchExcludablePartActions(storeId),
    }),
  validate: validateDiscountCode,
}

export default discountCodeQueries
